/**
 * Step - Purchase Details
 *
 * Renders purchase fields dynamically from a transaction template when
 * available, falling back to a hardcoded set when no template is linked.
 * All fields flow through generated `useForm`; field changes are
 * mirrored into the parent `WizardState` so downstream steps (Review,
 * submission) can read them.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Receipt, ChevronLeft, ArrowRight } from 'lucide-react'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { useUploadFile } from '@/admin/hooks/use-products'
import { Button } from '@/shared/components/ui/button'
import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import type { RegistrationFormMetadata } from '@/admin/types/products'
import type { DataCaptureConfig } from '@/admin/hooks/use-registration-settings'
import { isVisible, isRequired } from '@/admin/hooks/use-data-capture-config'
import { RegistrationPanel, RegistrationStepGrid } from './layout'
import { FILE_FIELD_TYPES, normaliseTemplateAttribute, useForm } from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { buildFormFieldFromMeta } from '@/shared/lib/forms/generated-metadata'

/* ── Props ───────────────────────────────────────────────── */

interface StepProps {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
  back?: () => void
  continueDisabled?: boolean
  continueHint?: string | null
  metadata?: RegistrationFormMetadata | null
  dataCaptureConfig?: DataCaptureConfig
  /**
   * Extra panel(s) to render alongside this step's own panel in the
   * shared `RegistrationStepGrid`.
   */
  extraPanels?: React.ReactNode
}

const FORM_NAME = 'admin-purchase-form'

/** Map a backend transaction-template code-name to the wizard-state key it
 *  mirrors. Anything not in the map is treated as a generic transaction
 *  attribute and stored under `state.transactionData[code_name]`. */
const TEMPLATE_CODE_TO_STATE_KEY: Record<string, keyof WizardState> = {
  purchase_date: 'purchaseDate',
  warranty_start_date: 'warrantyStartDate',
  sales_person: 'salesPerson',
  sales_reference: 'salesRefNumber',
}

/* ── Component ───────────────────────────────────────────── */

export function StepPurchase({
  state,
  update,
  next,
  back,
  // The legacy `continueDisabled` prop is intentionally not destructured.
  // Validation is owned by `useForm` and surfaces inline.
  continueHint,
  metadata,
  dataCaptureConfig,
  extraPanels,
}: StepProps) {
  const dcc = dataCaptureConfig
  const { currency: orgCurrency } = useFormatting()
  const uploadMutation = useUploadFile()

  /* --- Transaction template attrs --- */
  const rawAttrs = metadata?.transaction_template?.attributes
  const transactionAttrs = useMemo(() => {
    if (!rawAttrs) return null
    return [...rawAttrs].sort((a, b) => a.order - b.order)
  }, [rawAttrs])
  const hasTemplate = !!transactionAttrs

  /* Resolve the selected warranty template's type so attribute-level
   * visibility rules stored in configuration_data can be evaluated. */
  const selectedWarrantyType = useMemo(() => {
    if (!state.warrantyTemplateUuid || !metadata?.warranty_templates) return null
    const selected = metadata.warranty_templates.find(
      (t) => t.uuid === state.warrantyTemplateUuid,
    )
    return selected?.warranty_type ?? null
  }, [state.warrantyTemplateUuid, metadata?.warranty_templates])

  const normalisedAttrs = useMemo(
    () =>
      (transactionAttrs ?? []).map(normaliseTemplateAttribute).map((attr) => {
        // Patch: force PROOF_OF_PURCHASE on every file field in this step,
        // regardless of what the template's configuration_data says.
        // Templates currently mis-tag receipts as PRODUCT_DOCUMENT, which
        // the backend rejects for png/jpeg uploads.
        // TODO(file-use-cases): drop once the template config exposes
        // proper use-case + allowed-mime metadata end-to-end.
        if (FILE_FIELD_TYPES.has(attr.field_type.toUpperCase())) {
          return { ...attr, file_use_case: 'PROOF_OF_PURCHASE' }
        }
        return attr
      })
      .filter((attr) => !attr.configuration_data?.hidden)
      // Apply DataCaptureConfig overrides for DCC-governed fields:
      // - EXCLUDED / NONE  → hide the field entirely (excluded by this filter)
      // - OPTIONAL → show but override required=false (even if template says required)
      // - REQUIRED → keep template required flag as-is
      .filter((attr) => {
        if (attr.code_name === 'proof_of_purchase') return isVisible(dcc?.transaction_proof_of_purchase)
        if (attr.code_name === 'purchase_price') return isVisible(dcc?.transaction_price)
        if (attr.code_name === 'purchase_date') return isVisible(dcc?.transaction_date)
        // If the attribute declares a warranty-type allowlist in its
        // configuration_data, only show it when the selected warranty type
        // is in that list. This keeps the check data-driven and generic -
        // no field code_names or warranty type strings hardcoded here.
        const visibleForTypes = attr.configuration_data?.visible_for_warranty_types
        if (Array.isArray(visibleForTypes) && visibleForTypes.length > 0) {
          return selectedWarrantyType != null && visibleForTypes.includes(selectedWarrantyType)
        }
        return true
      })
      .map((attr) => {
        if (attr.code_name === 'proof_of_purchase') {
          if (dcc?.transaction_proof_of_purchase && !isRequired(dcc.transaction_proof_of_purchase)) {
            return { ...attr, required: false }
          }
        }
        if (attr.code_name === 'purchase_price') {
          if (dcc?.transaction_price && !isRequired(dcc.transaction_price)) {
            return { ...attr, required: false }
          }
        }
        if (attr.code_name === 'purchase_date') {
          if (dcc?.transaction_date && !isRequired(dcc.transaction_date)) {
            return { ...attr, required: false }
          }
        }
        return attr
      }),
    [transactionAttrs, dcc, selectedWarrantyType],
  )

  /* --- Field meta list ---
   * With a template: render the template attributes (whatever they are).
   * Without one: fall back to a hardcoded purchase-form shape.
   */
  const fieldsMeta = useMemo<FormFieldMeta[]>(() => {
    if (hasTemplate) return normalisedAttrs
    return [
      {
        code_name: 'warrantyStartDate',
        display_name: 'Warranty Start Date',
        field_type: 'DATE',
        required: false,
        description: 'Defaults to today if not set',
        order: 1,
      },
      { code_name: 'purchasePrice', display_name: 'Purchase Price', field_type: 'CURRENCY', required: false, order: 2 },
      { code_name: 'salesPerson', display_name: 'Salesperson', field_type: 'TEXT', required: false, hint: 'Optional', order: 3 },
      { code_name: 'salesRefNumber', display_name: 'Sales Reference', field_type: 'TEXT', required: false, hint: 'Optional', order: 4 },
    ]
  }, [hasTemplate, normalisedAttrs])

  /* --- Form integration.
   * The form is its own source of truth while the user types - we don't
   * touch `WizardState` per keystroke (that would re-render the wizard
   * host, churn props passed to this step, and remount the form's input
   * tree, killing focus and the locale-aware live formatting).
   * Instead, on submit we push everything into `WizardState` and only
   * then advance to the next step. */

  const initialFieldValues = useMemo(() => {
    const result: Record<string, unknown> = {}
    for (const meta of fieldsMeta) {
      const codeName = meta.code_name
      if (codeName === 'purchasePrice' || codeName === 'purchase_price') {
        const amount = state.purchasePrice ? Number.parseFloat(state.purchasePrice) : undefined
        result[codeName] = {
          amount: Number.isFinite(amount) ? amount : undefined,
          currency: state.priceCurrency || orgCurrency,
        }
        continue
      }
      const mapped = TEMPLATE_CODE_TO_STATE_KEY[codeName]
      if (mapped) {
        const raw = state[mapped]
        result[codeName] = raw
        continue
      }
      if (codeName in state) {
        result[codeName] = (state as unknown as Record<string, unknown>)[codeName]
        continue
      }
      result[codeName] = state.transactionData?.[codeName]
        ?? meta.configuration_data?.default_value
    }
    return result
    // Initial values are captured once per field-shape change so re-renders
    // of the wizard host don't push fresh objects into the form config and
    // remount the inputs. Live updates flow through the form state until
    // submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsMeta, orgCurrency])

  const generatedFields = useMemo(
    () =>
      fieldsMeta.map((meta) => buildFormFieldFromMeta(meta, initialFieldValues[meta.code_name])),
    [fieldsMeta, initialFieldValues],
  )

  // External refs - keep `next` + `update` + `uploadMutation` reachable
  // without putting them in form callback deps (each gets a new identity
  // per parent render).
  const uploadMutationRef = useRef(uploadMutation)
  const updateRef = useRef(update)
  const nextRef = useRef(next)
  const hasTemplateRef = useRef(hasTemplate)
  useEffect(() => {
    uploadMutationRef.current = uploadMutation
  })
  useEffect(() => {
    updateRef.current = update
  })
  useEffect(() => {
    nextRef.current = next
  })
  useEffect(() => {
    hasTemplateRef.current = hasTemplate
  })

  const renderEmptySubmit = useCallback(() => <></>, [])
  const formUploadFile = useCallback(
    async ({ file, use_case }: { file: File; use_case: string }) => {
      const configured = use_case || 'PROOF_OF_PURCHASE'
      const result = await uploadMutationRef.current.mutateAsync({ file, use_case: configured })
      return { ...result }
    },
    [],
  )

  // Translate raw form values into the WizardState shape. Shared by the
  // submit path (which advances) and the Back path (which persists without
  // advancing) so navigating away never drops what the user entered.
  const buildWizardPatch = useCallback(
    (
      values: Record<string, unknown>,
      uploads?: Record<string, unknown>,
    ): Partial<WizardState> => {
      const patch: Partial<WizardState> = {}
      const transactionData: Record<string, unknown> = {}
      // Rich file/photo envelopes for display in the review card. The submit
      // path supplies them via `uploads` (where `values` is already reduced to
      // a bare UUID); the Back/unmount path has no `uploads`, but `values`
      // still holds the rich object, so capture it from there too.
      const transactionUploads: Record<string, unknown> = { ...(uploads ?? {}) }
      for (const [codeName, value] of Object.entries(values)) {
        if (codeName === 'purchasePrice' || codeName === 'purchase_price') {
          const v = value as { amount?: number; currency?: string } | undefined
          patch.purchasePrice = v?.amount != null ? String(v.amount) : ''
          patch.priceCurrency = v?.currency ?? orgCurrency
        } else {
          const stateKey = TEMPLATE_CODE_TO_STATE_KEY[codeName]
          if (stateKey) {
            ;(patch as Record<string, unknown>)[stateKey] = (value as string) ?? ''
          }
        }
        if (hasTemplateRef.current) {
          transactionData[codeName] = value
          // Only treat genuine file/photo fields as upload envelopes - keying
          // off the field's declared type (not the value's shape) so an entity
          // reference (e.g. an ENTITY_SEARCH `{ uuid, name }`) that happens to
          // carry a `uuid` isn't misread as a file.
          const fieldMeta = fieldsMeta.find((f) => f.code_name === codeName)
          if (
            transactionUploads[codeName] === undefined &&
            fieldMeta &&
            FILE_FIELD_TYPES.has(fieldMeta.field_type.toUpperCase()) &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value)
          ) {
            transactionUploads[codeName] = value
          }
        }
      }
      if (hasTemplateRef.current) {
        patch.transactionData = transactionData
        patch.transactionUploads = transactionUploads
      }
      return patch
    },
    [orgCurrency, fieldsMeta],
  )

  // On submit: translate the form values back into WizardState shape,
  // then advance.
  const handleFormSubmit = useCallback(
    async (
      values: Record<string, unknown>,
      _ctx: unknown,
      uploads?: Record<string, unknown>,
    ) => {
      updateRef.current(buildWizardPatch(values, uploads))
      nextRef.current()
    },
    [buildWizardPatch],
  )

  const formConfig = useMemo(
    () => ({
      name: FORM_NAME,
      submit: renderEmptySubmit,
      fields: generatedFields,
      uploadFile: formUploadFile,
    }),
    [generatedFields, renderEmptySubmit, formUploadFile],
  )

  // Stable dependency array for useForm: an inline literal would be a new
  // reference every render, remounting the form (and wiping entered data) on
  // any parent re-render or background refetch.
  const formDeps = useMemo(
    () => [fieldsMeta.length, hasTemplate],
    [fieldsMeta.length, hasTemplate],
  )
  const { Form, ref: formRef } = useForm(formConfig, handleFormSubmit, formDeps)

  // Going Back must not lose work. The form is its own source of truth
  // until submit, so harvest the current values straight from the form
  // controller and mirror them into WizardState before navigating back.
  // No validation runs and no upload is forced here: a raw File (selected
  // but not yet uploaded) is preserved as-is so it re-hydrates and stays
  // visible when the user returns to this step.
  const persistFormToState = useCallback(() => {
    const values = formRef.current?.values
    if (!values) return
    updateRef.current(buildWizardPatch(values))
  }, [buildWizardPatch, formRef])

  const handleBack = useCallback(() => {
    persistFormToState()
    back?.()
  }, [persistFormToState, back])

  // Also persist on unmount so any other navigation away from this step
  // (not just the Back button) keeps the entered data.
  const persistFormToStateRef = useRef(persistFormToState)
  useEffect(() => {
    persistFormToStateRef.current = persistFormToState
  })
  useEffect(() => {
    return () => persistFormToStateRef.current()
  }, [])

  /* --- Inline product/customer info header --- */
  const inlineInfo = useMemo(() => {
    const productName = state.selectedProduct?.cached_short_name
    const customerName = [state.consumerFirstName, state.consumerLastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    if (productName && customerName) return `${productName} - ${customerName}`
    return productName || null
  }, [state.selectedProduct, state.consumerFirstName, state.consumerLastName])

  /* --- Render --- */
  return (
    <div>
      <RegistrationStepGrid>
        <RegistrationPanel
          title="Purchase Details"
          icon={<Receipt size={14} className="text-[var(--ink-3)]" />}
        >
          {inlineInfo && (
            <p className="m-0 text-[13px] text-[var(--ink-3)]">{inlineInfo}</p>
          )}
          <Form className="grid grid-cols-2 gap-4" />
        </RegistrationPanel>

        {extraPanels}
      </RegistrationStepGrid>

      <div className="mt-6 flex items-center justify-between border-t-[0.5px] border-t-[var(--rule)] pt-4">
        {back ? (
          <Button
            variant="ghost"
            type="button"
            onClick={handleBack}
            className="flex cursor-pointer items-center gap-1 border-none bg-transparent px-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)] transition-all duration-150 ease hover:text-[var(--ink)]"
          >
            <ChevronLeft size={13} />
            Back
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-col items-end gap-1">
          {continueHint ? (
            <p className="max-w-[320px] text-right text-[11px] text-[var(--status-error)]">
              {continueHint}
            </p>
          ) : null}
          <Button
            form={FORM_NAME}
            type="submit"
            className="flex h-9 cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 text-[10px] font-light uppercase tracking-[0.14em] text-white transition-all duration-150 ease hover:opacity-90"
          >
            Continue
            <ArrowRight size={13} />
          </Button>
        </div>
      </div>
    </div>
  )
}
